import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { Mail, Send, User, MessageSquare, Loader2 } from 'lucide-react';
import api from '../api/axios';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error(t('contact.error_required'));
      return;
    }
    if (!isValidEmail(email)) {
      toast.error(t('contact.error_invalid_email'));
      return;
    }
    if (message.trim().length < 10) {
      toast.error(t('contact.error_short_message'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/contact', { name, email, message });
      toast.success(t('contact.success'));
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      toast.error(t('contact.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-0 sm:pt-12 pb-20 bg-slate-50 font-sans">
      <Helmet>
        <title>{t('navbar.contact')} - Melodis Clinic</title>
      </Helmet>

      <div className="max-w-2xl mx-auto px-0 sm:px-4">

        {/* Hero strip */}
        <div className="flex flex-col items-center text-center px-6 pt-8 sm:pt-12 pb-6 sm:pb-8 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg shadow-coral/20 mb-4">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {t('contact.title')}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-500 max-w-sm">
            {t('contact.subtitle', { defaultValue: 'お気軽にご連絡ください。通常2〜3営業日以内にご返信いたします。' })}
          </p>
          {/* accent line */}
          <div className="mt-4 h-1 w-12 rounded-full bg-gradient-primary opacity-60" />
        </div>

        {/* Form card */}
        <div className="bg-white border-y sm:border border-slate-100 shadow-none sm:shadow-lg sm:shadow-slate-100/80 rounded-none sm:rounded-3xl px-5 sm:px-8 md:px-10 py-8 sm:py-10">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Name */}
            <div>
              <label className="flex items-center gap-2 text-slate-700 mb-2 text-sm font-semibold">
                <User className="w-4 h-4 text-coral opacity-70" />
                {t('contact.name')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-slate-800 placeholder-slate-300 focus:outline-none focus:border-coral focus:bg-white focus:shadow-lg focus:shadow-coral/5 transition-all"
                placeholder={t('contact.name_placeholder', { defaultValue: '' }) || undefined}
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center gap-2 text-slate-700 mb-2 text-sm font-semibold">
                <Mail className="w-4 h-4 text-coral opacity-70" />
                {t('contact.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-slate-800 placeholder-slate-300 focus:outline-none focus:border-coral focus:bg-white focus:shadow-lg focus:shadow-coral/5 transition-all"
                placeholder={t('contact.email_placeholder', { defaultValue: '' }) || undefined}
              />
            </div>

            {/* Message */}
            <div>
              <label className="flex items-center gap-2 text-slate-700 mb-2 text-sm font-semibold">
                <MessageSquare className="w-4 h-4 text-coral opacity-70" />
                {t('contact.message')}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-slate-800 placeholder-slate-300 focus:outline-none focus:border-coral focus:bg-white focus:shadow-lg focus:shadow-coral/5 transition-all resize-none"
                placeholder={t('contact.message_placeholder', { defaultValue: '' }) || undefined}
              />
              <p className="mt-1.5 text-xs text-slate-400">
                {t('contact.note', { defaultValue: '' })}
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-primary text-white font-bold text-base shadow-lg shadow-coral/20 hover:shadow-coral/30 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('contact.sending')}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t('contact.send')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
